import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  ProjectsRegistryApi,
  RegistryConfig,
  RegistryMeta,
  ValidationResult,
  DojoProduct,
  DojoEngagement,
  MergeResult,
  RegistryNotification,
} from './ProjectsRegistryApi';

export class ProjectsRegistryApiClient implements ProjectsRegistryApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('projects-registry');
  }

  private async get<T>(path: string): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as any;
      throw new Error(body?.error?.message ?? `Request failed with status ${res.status}`);
    }
    return res.json() as Promise<T>;
  }

  private async put<T>(path: string, body: Record<string, unknown>): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const respBody = (await res.json().catch(() => ({}))) as any;
      throw new Error(respBody?.error?.message ?? `Request failed with status ${res.status}`);
    }
    // 204 No Content
    if (res.status === 204) return undefined as unknown as T;
    return res.json() as Promise<T>;
  }

  private async post<T>(path: string, body: Record<string, unknown>): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const respBody = (await res.json().catch(() => ({}))) as any;
      throw new Error(respBody?.error?.message ?? `Request failed with status ${res.status}`);
    }
    return res.json() as Promise<T>;
  }

  async getRegistry(): Promise<{ config: RegistryConfig; meta: RegistryMeta }> {
    return this.get('/v1/registry');
  }

  async putRegistry(config: RegistryConfig): Promise<void> {
    await this.put('/v1/registry', config as unknown as Record<string, unknown>);
  }

  async getRegistryRaw(): Promise<{ content: string }> {
    return this.get('/v1/registry/raw');
  }

  async putRegistryRaw(content: string): Promise<void> {
    await this.put('/v1/registry/raw', { content });
  }

  async validateRegistry(content: string): Promise<ValidationResult> {
    return this.post('/v1/registry/validate', { content });
  }

  async getMeta(): Promise<RegistryMeta> {
    return this.get('/v1/registry/meta');
  }

  async getDojoProducts(): Promise<DojoProduct[]> {
    const data = await this.get<{ products: DojoProduct[] }>('/v1/defectdojo/products');
    return data.products;
  }

  async getDojoEngagements(productId: number): Promise<DojoEngagement[]> {
    const data = await this.get<{ engagements: DojoEngagement[] }>(`/v1/defectdojo/products/${productId}/engagements`);
    return data.engagements;
  }

  async mergeDojoProducts(sourceProductId: number, targetProductId: number, sourceName: string, targetName: string): Promise<MergeResult> {
    return this.post('/v1/defectdojo/merge', { sourceProductId, targetProductId, sourceName, targetName });
  }

  async createDojoProduct(name: string): Promise<DojoProduct> {
    return this.post('/v1/defectdojo/products', { name });
  }

  async getNotifications(page = 1, pageSize = 20): Promise<{ notifications: RegistryNotification[]; total: number; page: number; pageSize: number }> {
    return this.get(`/v1/notifications?page=${page}&pageSize=${pageSize}`);
  }

  async clearNotifications(): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const res = await this.fetchApi.fetch(`${baseUrl}/v1/notifications`, { method: 'DELETE' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as any;
      throw new Error(body?.error?.message ?? `Request failed with status ${res.status}`);
    }
  }
}
